/**
 * UID-263 (ADR-0087 D5) — the bulk "Add N priced to cart" confirm modal's
 * "don't show this again" localStorage suppression, verbatim key name from
 * the design source (`Fictiv Platform.dc.html:8134`/`:8139`).
 *
 * FAIL-OPEN by design (per the design's own guard, `.dc.html:8134`/`:8139`):
 * `localStorage` access is wrapped in try/catch and a throw (private
 * browsing / storage-disabled contexts) is treated as "not suppressed" —
 * show the modal / don't skip it — never fail CLOSED (silently skipping a
 * warning ahead of a bundle-mutating action would be the unsafe direction).
 */
const HIDE_BULK_CART_WARN_KEY = 'ff_hide_bulk_cart_warn'

/** `true` only when the flag is durably set to `'1'`. Any throw (storage
 * disabled, private browsing, etc.) is treated as "not suppressed." */
export function isBulkCartWarnSuppressed(): boolean {
  try {
    return window.localStorage.getItem(HIDE_BULK_CART_WARN_KEY) === '1'
  } catch {
    return false
  }
}

/** Sets the suppression flag. Swallows a throw (storage disabled) — a
 * failed write just means the warning keeps showing next time, which is the
 * safe direction, not a crash. */
export function suppressBulkCartWarn(): void {
  try {
    window.localStorage.setItem(HIDE_BULK_CART_WARN_KEY, '1')
  } catch {
    // Intentionally swallowed — see doc comment above.
  }
}

/**
 * Gate-5 W4 fix (UID-401) — a PURE predicate for whether
 * `RfqReviewPage.handleBulkConfirm` should call `suppressBulkCartWarn()` at
 * all, extracted specifically so it is unit-testable independent of
 * `BulkAddPricedConfirmModal`'s OWN "don't show this again" checkbox being
 * hidden for a cart-bound flow (`!isCartBoundFlow`). That modal-side guard
 * means `hideNext` can never actually be `true` for the `'checkout'`/
 * `'generate-quote'` intents THROUGH TODAY'S REAL UI — which is exactly why
 * a defect here (calling `suppressBulkCartWarn()` for ANY intent whenever
 * `hideNext` was true) was invisible in ordinary use: the leak only bites a
 * FUTURE caller — a modal change that starts passing `hideNext: true` for a
 * cart-bound flow, or a new caller of `handleBulkConfirm` entirely. Suppressing
 * the plain "Add N priced to cart" warning is granted (ADR-0087 D5) to ONLY
 * that one flow; the Checkout/"Generate quote" CTAs must NEVER be
 * suppressible (ADR-0125 D9).
 */
export function shouldSuppressBulkCartWarn(
  intent: 'plain' | 'checkout' | 'generate-quote' | null,
  hideNext: boolean,
): boolean {
  return hideNext && intent === 'plain'
}
